import { useEffect, useMemo, useRef, useState } from 'react';
import { ACTIVE_COMPANY_KEY, api } from '../api.js';
import BrandIdentity from '../components/BrandIdentity.jsx';
import { IconCheck, IconChevron, IconLink } from '../components/icons.jsx';
import { Button, Pill } from '../components/primitives.jsx';
import Beacon, { BeaconSlowNote } from '../components/Beacon.jsx';
import { PALETTE } from '../palette.js';
import { safeFailureMessage } from '../safe-failure.js';
import {
  curatedMetaAuthorizationPresentation,
  curatedMetaResourceCatalog,
  META_COMPANY_STORAGE_KEY,
  META_RETURN_STORAGE_KEY,
  safeCuratedMetaReturnPath,
  storedCuratedMetaReturnPath,
} from '../components/CuratedMetaConnection.jsx';

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

export function parseMetaCallbackUrl(value, origin = 'https://app.beyondleads.ai') {
  let url;
  try {
    url = new URL(String(value || ''), origin);
  } catch {
    return Object.freeze({ isCallback: false, input: null, scrubbedPath: '' });
  }
  if (url.pathname !== '/meta/callback') {
    return Object.freeze({ isCallback: false, input: null, scrubbedPath: '' });
  }
  return Object.freeze({
    isCallback: true,
    input: Object.freeze({
      code: url.searchParams.get('code'),
      state: url.searchParams.get('state'),
      providerError: url.searchParams.get('error'),
    }),
    scrubbedPath: '/meta/callback',
  });
}

export function captureAndScrubMetaCallbackInput({ location, history, title = '' } = {}) {
  const parsed = parseMetaCallbackUrl(location?.href, location?.origin);
  if (!parsed.isCallback) return null;
  history?.replaceState?.({}, title, parsed.scrubbedPath);
  return parsed.input;
}

function callbackCompanyId(session) {
  return session?.user?.activeCompany?.id
    || session?.user?.memberships?.[0]?.companyId
    || '';
}

export async function restoreCuratedMetaCallbackTenantContext({
  companyId,
  persist = (selectedCompanyId) => localStorage.setItem(ACTIVE_COMPANY_KEY, selectedCompanyId),
} = {}) {
  const targetCompanyId = String(companyId || '').trim();
  if (!targetCompanyId) {
    const error = new Error('Meta callback tenant context is unavailable');
    error.code = 'meta_callback_tenant_context_unavailable';
    throw error;
  }
  persist(targetCompanyId);
  return targetCompanyId;
}

function callbackReturnLabel(returnPath) {
  return returnPath.includes('/curated-campaign/') ? 'your campaign' : 'Company';
}

export function curatedMetaCallbackResultView(result = {}) {
  const returnPath = safeCuratedMetaReturnPath(result?.returnTo);
  const returnLabel = callbackReturnLabel(returnPath);
  if (result?.outcome === 'authorization_checking') {
    return Object.freeze({
      status: 'warning',
      message: 'Checking connection',
      detail: `BeyondLeads is verifying Meta authorization. Continue to ${returnLabel} to review the result.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  const authorization = curatedMetaAuthorizationPresentation(result);
  const catalog = curatedMetaResourceCatalog(result);
  const fallbackFullyReconciled = catalog?.connectionState === 'ready'
    && catalog?.reconciliationStatus === 'succeeded'
    && catalog?.refreshRequired === false;
  const outcome = result?.outcome || (fallbackFullyReconciled ? 'fully_reconciled' : '');
  if (authorization.state === 'checking') {
    return Object.freeze({
      status: 'warning',
      message: 'Checking connection',
      detail: `BeyondLeads is checking Meta authorization. Continue to ${returnLabel} to review the result.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (authorization.state === 'unknown') {
    return Object.freeze({
      status: 'warning',
      message: 'Authorization not checked',
      detail: `Continue to ${returnLabel}; a company owner or admin can check Meta authorization.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (authorization.state === 'temporarily_unavailable') {
    return Object.freeze({
      status: 'warning',
      message: 'Meta temporarily unavailable',
      detail: `The last known authorization state is preserved. Continue to ${returnLabel} and check again later.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (authorization.state === 'reauth_required') {
    return Object.freeze({
      status: 'warning',
      message: 'Reconnect required',
      detail: `Continue to ${returnLabel} and reconnect this company’s Meta account.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (authorization.state === 'disconnected') {
    return Object.freeze({
      status: 'warning',
      message: 'Disconnected',
      detail: `Continue to ${returnLabel} and reconnect this company’s Meta account.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (outcome === 'fully_reconciled') {
    return Object.freeze({
      status: 'success',
      message: authorization.state === 'expiring' ? 'Connected—expires soon' : 'Meta is connected',
      detail: `Returning you to ${returnLabel}.`,
      returnPath,
      returnLabel,
      autoReturn: true,
    });
  }
  if (outcome === 'refresh_in_progress') {
    return Object.freeze({
      status: 'success',
      message: authorization.state === 'expiring'
        ? 'Connected—expires soon'
        : 'Connected — updating resources',
      detail: `Returning you to ${returnLabel}. BeyondLeads will update the available Meta resources automatically.`,
      returnPath,
      returnLabel,
      autoReturn: true,
    });
  }
  if (outcome === 'credential_saved_refresh_incomplete') {
    return Object.freeze({
      status: 'warning',
      message: authorization.state === 'expiring'
        ? 'Connected—expires soon'
        : 'Meta connection saved',
      detail: `Meta remains connected. Continue to ${returnLabel}; an owner or admin can retry synchronization if the background update fails.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  return Object.freeze({
    status: 'warning',
    message: 'Meta connection status needs review',
    detail: `Continue to ${returnLabel} to review the committed connection state.`,
    returnPath,
    returnLabel,
    autoReturn: false,
  });
}

export function curatedMetaCallbackErrorView(error, returnTo = '/#/company/connections') {
  const returnPath = safeCuratedMetaReturnPath(returnTo);
  const returnLabel = callbackReturnLabel(returnPath);
  if (error?.outcome === 'oauth_callback_in_progress') {
    const retryAfterSeconds = Number.isSafeInteger(error?.retryAfterSeconds)
      && error.retryAfterSeconds > 0
      ? Math.min(error.retryAfterSeconds, 30)
      : 30;
    return Object.freeze({
      status: 'warning',
      message: 'Meta connection is still being completed',
      detail: `Wait up to ${retryAfterSeconds} seconds, then return to ${returnLabel} to review the connection.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  if (error?.outcome === 'no_credential_change' && error?.credentialChanged === false) {
    return Object.freeze({
      status: 'error',
      message: 'Meta connection was not changed',
      detail: `${safeFailureMessage(error, 'integration')} Return to ${returnLabel} to try again.`,
      returnPath,
      returnLabel,
      autoReturn: false,
    });
  }
  return Object.freeze({
    status: 'error',
    message: 'Meta connection status could not be confirmed',
    detail: `${safeFailureMessage(error, 'integration')} Return to ${returnLabel} to review the connection before retrying.`,
    returnPath,
    returnLabel,
    autoReturn: false,
  });
}

export function runtimeEnvironment(hostname = window.location.hostname) {
  if (hostname === 'staging.beyondleads.ai') return 'staging';
  if (hostname === 'app.beyondleads.ai') return 'production';
  return 'local';
}

function firstSelection(options = {}) {
  const adAccount = options.adAccounts?.find((account) => account.pixels?.length) || options.adAccounts?.[0];
  return {
    adAccountId: adAccount?.id || '',
    pageId: options.pages?.[0]?.id || '',
    pixelId: adAccount?.pixels?.[0]?.id || '',
  };
}

export function MetaAssetSelection({ connection, companyId, onComplete, onCancel }) {
  const [selection, setSelection] = useState(() => firstSelection(connection?.options));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const adAccounts = connection?.options?.adAccounts || [];
  const pages = connection?.options?.pages || [];
  const selectedAdAccount = useMemo(
    () => adAccounts.find((account) => account.id === selection.adAccountId) || null,
    [adAccounts, selection.adAccountId],
  );
  const pixels = selectedAdAccount?.pixels || [];
  const canSubmit = Boolean(selection.adAccountId && selection.pageId && selection.pixelId);

  function selectAdAccount(adAccountId) {
    const account = adAccounts.find((item) => item.id === adAccountId);
    setSelection((current) => ({
      ...current,
      adAccountId,
      pixelId: account?.pixels?.[0]?.id || '',
    }));
  }

  async function submit(event) {
    event.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(
        '/api/saas/integrations/meta/select-assets',
        { handoff: connection.handoff, selection },
        tenantHeaders(companyId),
      );
      await onComplete(result.credential);
    } catch (err) {
      setError(safeFailureMessage(err, 'integration'));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={selectionFormStyle}>
      <div>
        <Pill tone="accent">One last step</Pill>
        <h1 style={titleStyle}>Choose where campaigns publish</h1>
        <p style={bodyStyle}>
          {connection?.identity?.name || 'Your Meta account'} has more than one available business asset.
          Select the company-owned ad account, Page, and pixel Beyond Leads should use.
        </p>
      </div>

      <div style={fieldGridStyle}>
        <SelectionField label="Ad account" value={selection.adAccountId} onChange={selectAdAccount} disabled={busy}>
          {adAccounts.map((account) => (
            <option key={account.id} value={account.id}>
              {account.name}{account.business?.name ? ` - ${account.business.name}` : ''}
            </option>
          ))}
        </SelectionField>
        <SelectionField
          label="Facebook Page"
          value={selection.pageId}
          onChange={(pageId) => setSelection((current) => ({ ...current, pageId }))}
          disabled={busy}
        >
          {pages.map((page) => <option key={page.id} value={page.id}>{page.name}</option>)}
        </SelectionField>
        <SelectionField
          label="Pixel"
          value={selection.pixelId}
          onChange={(pixelId) => setSelection((current) => ({ ...current, pixelId }))}
          disabled={busy || !selection.adAccountId}
        >
          {pixels.map((pixel) => <option key={pixel.id} value={pixel.id}>{pixel.name}</option>)}
        </SelectionField>
      </div>

      {!canSubmit && (
        <div style={noticeStyle} role="status">
          This Meta profile does not expose all required company assets. Confirm it can access an ad account,
          Facebook Page, and pixel, then reconnect.
        </div>
      )}
      {error && <div style={errorStyle} role="alert">{error}</div>}

      <div style={actionsStyle}>
        <Button type="submit" variant="accent" disabled={!canSubmit || busy}>
          <IconLink size={15} />
          {busy ? 'Connecting' : 'Connect account'}
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>Back to Settings</Button>
      </div>
    </form>
  );
}

function SelectionField({ label, value, onChange, disabled, children }) {
  return (
    <label style={fieldStyle}>
      <span style={fieldLabelStyle}>{label}</span>
      <span style={{ position: 'relative' }}>
        <select
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          style={selectStyle}
        >
          {!value && <option value="">No available option</option>}
          {children}
        </select>
        <span style={selectIconStyle} aria-hidden="true"><IconChevron size={15} /></span>
      </span>
    </label>
  );
}

function LegacyMetaConnectionCallback({ session, callbackInput }) {
  const startedRef = useRef(false);
  const [view, setView] = useState({ status: 'loading', message: 'Finishing your Meta connection' });
  const fallbackCompanyId = callbackCompanyId(session);

  function returnToSettings() {
    sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
    window.location.replace('/#/settings');
  }

  async function finishConnection(companyId, credential) {
    setView({ status: 'loading', message: 'Confirming account access' });
    sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
    setView({ status: 'success', message: 'Meta is connected' });
    window.setTimeout(() => window.location.replace('/#/settings'), 350);
  }

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = callbackInput?.code;
    const state = callbackInput?.state;
    const providerError = callbackInput?.providerError;
    const companyId = sessionStorage.getItem(META_COMPANY_STORAGE_KEY) || fallbackCompanyId;
    const redirectUri = new URL('/meta/callback', window.location.origin).toString();

    if (providerError) {
      setView({ status: 'error', message: 'Meta sign-in was cancelled or could not be completed.' });
      return;
    }
    if (!code || !state || !companyId) {
      setView({ status: 'error', message: 'This Meta connection request is incomplete. Return to Settings and try again.' });
      return;
    }

    api.post(
      '/api/saas/integrations/meta/callback',
      { code, state, redirectUri, environment: runtimeEnvironment() },
      tenantHeaders(companyId),
    ).then(async (result) => {
      if (result?.selectionRequired) {
        setView({ status: 'selection', companyId, connection: result });
        return;
      }
      await finishConnection(companyId, result?.credential);
    }).catch((err) => {
      setView({ status: 'error', message: safeFailureMessage(err, 'integration') });
    });
  }, [callbackInput, fallbackCompanyId]);

  return (
    <main style={screenStyle}>
      <div style={brandStyle}>
        <div style={logoStyle}>r</div>
        <div>
          <div style={productStyle}>Remodel AI</div>
          <div style={eyebrowStyle}>Lead Platform</div>
        </div>
      </div>

      <section style={panelStyle}>
        {view.status === 'selection' ? (
          <MetaAssetSelection
            connection={view.connection}
            companyId={view.companyId}
            onComplete={(credential) => finishConnection(view.companyId, credential)}
            onCancel={returnToSettings}
          />
        ) : (
          <ConnectionState status={view.status} message={view.message} onReturn={returnToSettings} />
        )}
      </section>
    </main>
  );
}

export function CuratedMetaConnectionCallback({ session, callbackInput }) {
  const callbackInputRef = useRef(callbackInput || null);
  const callbackRequestRef = useRef(null);
  const [view, setView] = useState({ status: 'loading', message: 'Finishing your Meta connection' });
  const fallbackCompanyId = callbackCompanyId(session);

  function returnToDestination() {
    sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
    sessionStorage.removeItem(META_RETURN_STORAGE_KEY);
    window.location.replace(view.returnPath || '/#/company/connections');
  }

  useEffect(() => {
    let active = true;
    let returnTimer;

    const {
      code,
      state,
      providerError,
    } = callbackInputRef.current || {};
    const companyId = sessionStorage.getItem(META_COMPANY_STORAGE_KEY) || fallbackCompanyId;
    const returnPath = storedCuratedMetaReturnPath(sessionStorage, companyId);
    const returnLabel = callbackReturnLabel(returnPath);
    const redirectUri = new URL('/meta/callback', window.location.origin).toString();

    if (providerError) {
      sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
      setView({
        status: 'error',
        message: 'Meta sign-in was cancelled',
        detail: `No connection changes were requested. Return to ${returnLabel} when you are ready.`,
        returnPath,
        returnLabel,
      });
      return () => { active = false; };
    }
    if (!code || !state || !companyId) {
      sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
      setView({
        status: 'error',
        message: 'This Meta connection request is incomplete',
        detail: 'No connection request was completed. Return to Company and try again.',
        returnPath: '/#/company/connections',
      });
      return () => { active = false; };
    }

    if (!callbackRequestRef.current) {
      callbackRequestRef.current = api.post(
        '/api/saas/meta/connection/callback',
        { code, state, redirectUri },
        tenantHeaders(companyId),
      );
    }
    callbackRequestRef.current.then(async (result) => {
      if (!active) return;
      const nextView = curatedMetaCallbackResultView(result);
      try {
        await restoreCuratedMetaCallbackTenantContext({ companyId });
      } catch {
        if (!active) return;
        sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
        setView({
          status: 'warning',
          message: nextView.message,
          detail: 'The Meta connection was saved, but the company could not be reopened automatically. Return to Company and select it before continuing.',
          returnPath: '/#/company/connections',
          returnLabel: 'Company',
          autoReturn: false,
        });
        return;
      }
      if (!active) return;
      sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
      sessionStorage.removeItem(META_RETURN_STORAGE_KEY);
      setView(nextView);
      if (nextView.autoReturn) {
        returnTimer = window.setTimeout(() => window.location.replace(nextView.returnPath), 350);
      }
    }).catch((error) => {
      if (!active) return;
      sessionStorage.removeItem(META_COMPANY_STORAGE_KEY);
      setView(curatedMetaCallbackErrorView(error, returnPath));
    });

    return () => {
      active = false;
      if (returnTimer) window.clearTimeout(returnTimer);
    };
  }, [fallbackCompanyId]);

  const failed = view.status === 'error';
  const complete = view.status === 'success';
  const needsAction = view.status === 'warning';
  return (
    <main className="bl-brand-surface" style={curatedScreenStyle}>
      <BrandIdentity detail="Curated Meta ads" />

      <section style={panelStyle}>
        <div style={stateStyle} role={failed ? 'alert' : 'status'} aria-live="polite" aria-busy={!complete && !failed && !needsAction ? 'true' : undefined}>
          {complete ? (
            <span style={successIconStyle}><IconCheck size={22} /></span>
          ) : failed || needsAction ? (
            <span style={errorIconStyle}>!</span>
          ) : (
            <Beacon size="page" delayMs={0} slowAfterMs={null} />
          )}
          <h1 style={{ ...titleStyle, marginBottom: 0 }}>{view.message}</h1>
          <p style={{ ...bodyStyle, maxWidth: 470 }}>
            {view.detail || 'Keep this window open while BeyondLeads securely finishes the company connection and refreshes Meta resources.'}
            {!complete && !failed && !needsAction ? <><br /><BeaconSlowNote /></> : null}
          </p>
          {(failed || needsAction) && (
            <Button type="button" variant={needsAction ? 'accent' : 'ghost'} onClick={returnToDestination}>
              {needsAction ? `Continue to ${view.returnLabel || 'Company'}` : `Back to ${view.returnLabel || 'Company'}`}
            </Button>
          )}
        </div>
      </section>
    </main>
  );
}

export default function MetaConnectionCallback({ session, callbackInput }) {
  return session?.user?.auth?.type === 'legacy'
    ? <LegacyMetaConnectionCallback session={session} callbackInput={callbackInput} />
    : <CuratedMetaConnectionCallback session={session} callbackInput={callbackInput} />;
}

function ConnectionState({ status, message, onReturn }) {
  const failed = status === 'error';
  const complete = status === 'success';
  return (
    <div style={stateStyle} role={failed ? 'alert' : 'status'} aria-live="polite" aria-busy={!complete && !failed ? 'true' : undefined}>
      {complete ? (
        <span style={successIconStyle}><IconCheck size={22} /></span>
      ) : failed ? (
        <span style={errorIconStyle}>!</span>
      ) : (
        <Beacon size="page" delayMs={0} slowAfterMs={null} />
      )}
      <h1 style={{ ...titleStyle, marginBottom: 0 }}>{message}</h1>
      <p style={{ ...bodyStyle, maxWidth: 470 }}>
        {failed
          ? 'No credentials were changed. You can safely return and start the connection again.'
          : complete
            ? 'Returning you to company Settings.'
            : 'Keep this window open while Beyond Leads verifies the company account.'}
        {!complete && !failed ? <><br /><BeaconSlowNote /></> : null}
      </p>
      {failed && <Button type="button" variant="ghost" onClick={onReturn}>Back to Settings</Button>}
    </div>
  );
}

const screenStyle = {
  minHeight: '100vh',
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  gap: 22,
  padding: 'clamp(20px, 4vw, 44px)',
  boxSizing: 'border-box',
  background: PALETTE.bg,
  color: PALETTE.ink,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
};

const curatedScreenStyle = {
  ...screenStyle,
  fontFamily: 'var(--active-font-ui, Inter, sans-serif)',
};

const brandStyle = { display: 'flex', alignItems: 'center', gap: 10 };
const logoStyle = {
  width: 38,
  height: 38,
  borderRadius: 9,
  display: 'grid',
  placeItems: 'center',
  background: PALETTE.ink,
  color: PALETTE.accent,
  fontFamily: 'Instrument Serif, serif',
  fontSize: 24,
  fontStyle: 'italic',
};
const productStyle = { fontSize: 15, fontWeight: 650 };
const eyebrowStyle = { marginTop: 2, color: PALETTE.ink3, fontSize: 10.5, textTransform: 'uppercase' };
const panelStyle = {
  width: 'min(100%, 780px)',
  alignSelf: 'center',
  justifySelf: 'center',
  padding: 'clamp(24px, 5vw, 48px)',
  boxSizing: 'border-box',
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 12,
  background: PALETTE.surface,
  boxShadow: '0 18px 50px rgba(26, 23, 20, 0.08)',
};
const selectionFormStyle = { display: 'grid', gap: 28 };
const titleStyle = {
  margin: '14px 0 10px',
  color: PALETTE.ink,
  fontFamily: 'Instrument Serif, serif',
  fontSize: 'clamp(34px, 7vw, 52px)',
  fontWeight: 400,
  lineHeight: 1.02,
  letterSpacing: 0,
};
const bodyStyle = { margin: 0, color: PALETTE.ink2, fontSize: 14, lineHeight: 1.6 };
const fieldGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))', gap: 14 };
const fieldStyle = { display: 'grid', gap: 7 };
const fieldLabelStyle = { color: PALETTE.ink3, fontSize: 11, fontWeight: 650, textTransform: 'uppercase' };
const selectStyle = {
  width: '100%',
  minHeight: 44,
  appearance: 'none',
  padding: '10px 38px 10px 12px',
  border: `1px solid ${PALETTE.controlBorder}`,
  borderRadius: 8,
  background: PALETTE.surface,
  color: PALETTE.ink,
  font: '500 13px var(--active-font-ui, Inter, sans-serif)',
};
const selectIconStyle = { position: 'absolute', right: 12, top: 14, pointerEvents: 'none', transform: 'rotate(90deg)', color: PALETTE.ink3 };
const noticeStyle = { padding: 13, borderRadius: 8, border: '1px solid #EBDCB2', background: PALETTE.warnSoft, color: PALETTE.ink2, fontSize: 12.5, lineHeight: 1.45 };
const errorStyle = { padding: 13, borderRadius: 8, border: '1px solid #E8C9C1', background: PALETTE.dangerSoft, color: PALETTE.danger, fontSize: 12.5 };
const actionsStyle = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' };
const stateStyle = { minHeight: 300, display: 'grid', justifyItems: 'center', alignContent: 'center', gap: 16, textAlign: 'center' };
const successIconStyle = { width: 46, height: 46, display: 'grid', placeItems: 'center', borderRadius: '50%', color: PALETTE.success, background: PALETTE.successSoft };
const errorIconStyle = { width: 46, height: 46, display: 'grid', placeItems: 'center', borderRadius: '50%', color: PALETTE.danger, background: PALETTE.dangerSoft, fontSize: 24, fontWeight: 700 };
